import { useState, useCallback, useEffect, useRef } from "react";

// One retry, no backoff, non-404 only. /DashboardPublic/All and
// /DashboardAdmin/GetOrgAll run ~16 parallel queries plus an in-memory
// aggregation on a 29s-capped API Gateway route, so the FIRST request after the
// function has gone idle (JIT-cold, no warm connection pool) is the one that can
// exceed the ceiling and come back 502/504. That request warms the container, so
// attempt 2 succeeds — where the single-attempt version dead-ended the whole page
// on a "Something went wrong" screen the viewer could only escape by reloading.
// 404 is never retried: that is a revoked or expired share link, and it must
// reach the invalid-link screen on the first answer.
async function fetchWithOneRetry(fetchFn) {
  try {
    return await fetchFn();
  } catch (err) {
    if (err?.response?.status === 404) throw err;
    return fetchFn();
  }
}

export function useDashboardData(fetchFn, deps = [], { silentRefetchMs } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const isFirstLoad = useRef(true);
  const accessRevoked = useRef(false);

  const load = useCallback(async () => {
    // A revoked grant is terminal. Without this guard the silent interval — or
    // an already-scheduled load — would clear `notFound` on line ~19 and flash
    // the dashboard back over the invalid-link screen.
    if (accessRevoked.current) return;
    // First load ever -> full `loading` (callers show a page loader). Every
    // later load (client switch, date-range change, silent interval) is a
    // `refetching` -> callers keep the existing dashboard mounted and show a
    // subtle inline indicator instead of tearing the page down.
    if (isFirstLoad.current) setLoading(true);
    else setRefetching(true);
    setError(null);
    setNotFound(false);
    try {
      const result = await fetchWithOneRetry(fetchFn);
      setData(result);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
      setRefetching(false);
      isFirstLoad.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!silentRefetchMs) return undefined;
    const interval = setInterval(load, silentRefetchMs);
    return () => clearInterval(interval);
  }, [load, silentRefetchMs]);

  // Called when any request on the page — not just this hook's own fetch —
  // proves the viewing grant is gone (see dashboardApi's guard). Lets a list
  // call from any section, or the tab-switch liveness ping, put the page into
  // the same invalid-link state the initial load would have produced.
  const markAccessRevoked = useCallback(() => {
    accessRevoked.current = true;
    setNotFound(true);
    setLoading(false);
    setRefetching(false);
  }, []);

  return { data, loading, refetching, error, notFound, refetch: load, markAccessRevoked };
}
